import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/contexts/I18nContext";
import { Lock, LogOut, Trash2, Pencil, Plus, Upload, X, Store } from "lucide-react";

interface MerchantInfo {
  id: number;
  name: string;
  slug: string;
}

interface MerchantListing {
  id: number;
  slug: string | null;
  title: string;
  type: string;
  categoryId: number;
  categoryName: string;
  area: string;
  description: string;
  imageUrl: string;
  images: string[];
  priceAed: number | null;
  isFeatured: boolean;
  isPublished: boolean;
  createdAt: string;
}

interface Category {
  id: number;
  name: string;
}

const MAX_IMAGES = 5;

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!response.ok) {
    let message = "Request failed";
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // keep default message
    }
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function ListingFormDialog({
  open,
  onClose,
  categories,
  listing,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  listing: MerchantListing | null;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"product" | "service">("product");
  const [categoryId, setCategoryId] = useState<string>("");
  const [area, setArea] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(listing?.title ?? "");
    setType((listing?.type as "product" | "service") ?? "product");
    setCategoryId(listing ? String(listing.categoryId) : "");
    setArea(listing?.area ?? "");
    setDescription(listing?.description ?? "");
    setPrice(listing?.priceAed != null ? String(listing.priceAed) : "");
    setImages(listing?.images?.length ? listing.images : listing?.imageUrl ? [listing.imageUrl] : []);
  }, [open, listing]);

  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const selected = Array.from(files);
    if (images.length + selected.length > MAX_IMAGES) {
      toast({
        title: t("You can upload up to 5 photos per listing"),
        description: t(`You can add ${Math.max(0, MAX_IMAGES - images.length)} more.`),
        variant: "destructive",
      });
      return;
    }
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of selected) {
        const ticket = await api<{ uploadURL: string; objectPath: string }>(
          "/api/merchant/listing-images/upload-url",
          { method: "POST", body: JSON.stringify({ size: file.size, contentType: file.type }) },
        );
        const response = await fetch(ticket.uploadURL, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!response.ok) throw new Error("upload_failed");
        const published = await api<{ objectPath: string }>("/api/merchant/listing-images/publish", {
          method: "POST",
          body: JSON.stringify({ objectPath: ticket.objectPath }),
        });
        uploaded.push(`/api/storage${published.objectPath}`);
      }
      setImages((prev) => [...prev, ...uploaded]);
      toast({ title: selected.length === 1 ? t("Photo uploaded") : t("Photos uploaded") });
    } catch {
      toast({ title: t("Photo upload failed"), description: t("Please try again."), variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!title.trim() || !categoryId || !area.trim() || !description.trim() || images.length === 0) {
      toast({ title: t("Please fill in all fields and add at least one photo"), variant: "destructive" });
      return;
    }
    const priceValue = price.trim() === "" ? null : Number(price);
    if (priceValue !== null && (!Number.isFinite(priceValue) || priceValue < 0)) {
      toast({ title: t("Price must be a valid number"), variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        type,
        categoryId: Number(categoryId),
        area: area.trim(),
        description: description.trim(),
        imageUrl: images[0],
        images,
        priceAed: priceValue,
      };
      if (listing) {
        await api(`/api/merchant/listings/${listing.id}`, { method: "PATCH", body: JSON.stringify(body) });
        toast({ title: t("Listing updated") });
      } else {
        await api("/api/merchant/listings", { method: "POST", body: JSON.stringify(body) });
        toast({ title: t("Listing created"), description: t("It will appear publicly once approved.") });
      }
      onSaved();
      onClose();
    } catch (error) {
      toast({ title: t("Could not save listing"), description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{listing ? t("Edit listing") : t("New listing")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">{t("Title")}</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="input-merchant-listing-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">{t("Type")}</label>
              <Select value={type} onValueChange={(value) => setType(value as "product" | "service")}>
                <SelectTrigger data-testid="select-merchant-listing-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">{t("Product")}</SelectItem>
                  <SelectItem value="service">{t("Service")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t("Category")}</label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger data-testid="select-merchant-listing-category"><SelectValue placeholder={t("Select")} /></SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">{t("Area")}</label>
              <Input value={area} onChange={(e) => setArea(e.target.value)} data-testid="input-merchant-listing-area" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("Price (AED)")}</label>
              <Input
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputMode="decimal"
                placeholder={t("Optional")}
                data-testid="input-merchant-listing-price"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">{t("Description")}</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} data-testid="input-merchant-listing-description" />
          </div>
          <div>
            <label className="text-sm font-medium">{t("Photos")}</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {images.map((image, index) => (
                <div key={`${image}-${index}`} className="relative w-20 h-20 rounded-md overflow-hidden border">
                  <img src={image} alt="" className="w-full h-full object-cover" />
                  {index === 0 && (
                    <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] text-center">{t("Cover")}</span>
                  )}
                  <button
                    type="button"
                    className="absolute top-0.5 end-0.5 bg-black/60 text-white rounded-full p-0.5"
                    onClick={() => setImages((prev) => prev.filter((_, i) => i !== index))}
                    data-testid={`btn-remove-merchant-photo-${index}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {images.length < MAX_IMAGES && (
                <button
                  type="button"
                  className="w-20 h-20 rounded-md border border-dashed flex flex-col items-center justify-center text-muted-foreground text-xs gap-1 disabled:opacity-50"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="btn-upload-merchant-photo"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? t("Uploading…") : t("Add")}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(event) => { void uploadFiles(event.target.files); }}
              data-testid="input-merchant-photo-upload"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
          <Button onClick={() => { void save(); }} disabled={saving || uploading} data-testid="btn-save-merchant-listing">
            {saving ? t("Saving…") : t("Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MerchantPortalPage() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [checkingSession, setCheckingSession] = useState(true);
  const [merchant, setMerchant] = useState<MerchantInfo | null>(null);
  const [slug, setSlug] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [listings, setListings] = useState<MerchantListing[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MerchantListing | null>(null);
  const [deleting, setDeleting] = useState<MerchantListing | null>(null);

  const loadListings = useCallback(async () => {
    setLoadingListings(true);
    try {
      const [listingsResponse, categoriesResponse] = await Promise.all([
        api<{ listings: MerchantListing[] }>("/api/merchant/listings"),
        api<Category[]>("/api/merchant/categories"),
      ]);
      setListings(listingsResponse.listings);
      setCategories(categoriesResponse);
    } catch (error) {
      const status = (error as Error & { status?: number }).status;
      if (status === 401) {
        setMerchant(null);
      } else {
        toast({ title: t("Could not load your listings"), variant: "destructive" });
      }
    } finally {
      setLoadingListings(false);
    }
  }, [t, toast]);

  useEffect(() => {
    api<MerchantInfo>("/api/merchant/session")
      .then((info) => {
        setMerchant(info);
      })
      .catch(() => {
        setMerchant(null);
      })
      .finally(() => {
        setCheckingSession(false);
      });
  }, []);

  useEffect(() => {
    if (merchant) void loadListings();
  }, [merchant, loadListings]);

  const login = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoginError(null);
    setLoggingIn(true);
    try {
      const info = await api<MerchantInfo>("/api/merchant/session", {
        method: "POST",
        body: JSON.stringify({ slug: slug.trim(), password }),
      });
      setMerchant(info);
      setPassword("");
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : t("Login failed"));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await fetch("/api/merchant/session", { method: "DELETE", credentials: "same-origin" }).catch(() => undefined);
    setMerchant(null);
    setListings([]);
  };

  const togglePublish = async (listing: MerchantListing) => {
    const next = !listing.isPublished;
    setListings((prev) => prev.map((item) => (item.id === listing.id ? { ...item, isPublished: next } : item)));
    try {
      await api(`/api/merchant/listings/${listing.id}/publish`, {
        method: "PATCH",
        body: JSON.stringify({ isPublished: next }),
      });
    } catch {
      setListings((prev) => prev.map((item) => (item.id === listing.id ? { ...item, isPublished: !next } : item)));
      toast({ title: t("Could not update listing"), variant: "destructive" });
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api(`/api/merchant/listings/${deleting.id}`, { method: "DELETE" });
      setListings((prev) => prev.filter((item) => item.id !== deleting.id));
      toast({ title: t("Listing deleted") });
    } catch {
      toast({ title: t("Could not delete listing"), variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  if (checkingSession) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{t("Loading…")}</div>;
  }

  if (!merchant) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
              <Store className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>{t("Merchant Portal")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("Sign in to manage your listings")}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={(event) => { void login(event); }} className="space-y-3">
              <div>
                <label className="text-sm font-medium">{t("Business slug")}</label>
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder={t("your-business-name")}
                  autoComplete="username"
                  data-testid="input-merchant-slug"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t("Password")}</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="input-merchant-password"
                />
              </div>
              {loginError && <p className="text-sm text-destructive" data-testid="text-merchant-login-error">{loginError}</p>}
              <Button type="submit" className="w-full" disabled={loggingIn || !slug.trim() || !password} data-testid="btn-merchant-login">
                <Lock className="w-4 h-4 me-2" />
                {loggingIn ? t("Signing in…") : t("Sign in")}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                {t("Need access? Contact the Souq Crypto team to set up your portal password.")}
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-background">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold" data-testid="text-merchant-portal-title">{merchant.name}</h1>
            <p className="text-sm text-muted-foreground">{t("Merchant Portal")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="btn-new-merchant-listing">
              <Plus className="w-4 h-4 me-1" />
              {t("New listing")}
            </Button>
            <Button variant="outline" onClick={() => { void logout(); }} data-testid="btn-merchant-logout">
              <LogOut className="w-4 h-4 me-1" />
              {t("Log out")}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("Your listings")}</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingListings ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t("Loading…")}</p>
            ) : listings.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm text-muted-foreground mb-3">{t("You don't have any listings yet.")}</p>
                <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
                  <Plus className="w-4 h-4 me-1" />
                  {t("Create your first listing")}
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Listing")}</TableHead>
                      <TableHead>{t("Category")}</TableHead>
                      <TableHead>{t("Price")}</TableHead>
                      <TableHead>{t("Status")}</TableHead>
                      <TableHead>{t("Visible")}</TableHead>
                      <TableHead className="text-end">{t("Actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listings.map((listing) => (
                      <TableRow key={listing.id} data-testid={`row-merchant-listing-${listing.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <img src={listing.imageUrl} alt="" className="w-10 h-10 rounded object-cover border" />
                            <div>
                              <p className="font-medium">{listing.title}</p>
                              <p className="text-xs text-muted-foreground">{listing.area}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{listing.categoryName}</TableCell>
                        <TableCell>{listing.priceAed != null ? `AED ${listing.priceAed.toLocaleString()}` : "—"}</TableCell>
                        <TableCell>
                          {listing.isPublished ? (
                            <Badge variant="default">{t("Live")}</Badge>
                          ) : (
                            <Badge variant="secondary">{t("Hidden")}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={listing.isPublished}
                            onCheckedChange={() => { void togglePublish(listing); }}
                            data-testid={`switch-merchant-publish-${listing.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-end">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setEditing(listing); setFormOpen(true); }}
                              data-testid={`btn-edit-merchant-listing-${listing.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleting(listing)}
                              data-testid={`btn-delete-merchant-listing-${listing.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        <p className="text-xs text-muted-foreground mt-3">
          {t("New listings start hidden until the Souq Crypto team reviews and publishes them.")}
        </p>
      </main>

      <ListingFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        categories={categories}
        listing={editing}
        onSaved={() => { void loadListings(); }}
      />

      <Dialog open={deleting !== null} onOpenChange={(value) => { if (!value) setDeleting(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("Delete listing?")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deleting ? t(`"${deleting.title}" will be permanently removed.`) : null}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>{t("Cancel")}</Button>
            <Button variant="destructive" onClick={() => { void confirmDelete(); }} data-testid="btn-confirm-delete-merchant-listing">
              {t("Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
